import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Compass, Users, Heart, Sparkles, Calendar, Map } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "Custom Tour Packages", item: "https://tripsyfy.com/custom-tour-packages" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "What is a custom tour package from Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "A custom tour package is a travel itinerary built specifically around your travel dates, destinations, budget, group size, and interests. Unlike fixed group tours, a custom package is designed exclusively for you — with the hotels, activities, and pacing you want." } },
    { "@type": "Question", name: "How long does it take to build a custom itinerary?", acceptedAnswer: { "@type": "Answer", text: "For most destinations, we can present an initial custom itinerary within 2–5 business days of your request. Complex multi-country or multi-week trips may take a few extra days. We iterate with you until the plan is exactly right." } },
    { "@type": "Question", name: "Can Tripsyfy build a custom tour for a family with children?", acceptedAnswer: { "@type": "Answer", text: "Yes. Family travel is one of our specialties. We design itineraries that balance adult interests with child-friendly activities, manage the pace appropriately for families with young children, and book family-suitable accommodations." } },
    { "@type": "Question", name: "What destinations does Tripsyfy design custom tours for?", acceptedAnswer: { "@type": "Answer", text: "We build custom itineraries for destinations worldwide — including India, Europe, Southeast Asia, Africa (safaris), Japan, South America, the Caribbean, and the Middle East. If you have a destination in mind, we can build around it." } },
    { "@type": "Question", name: "Can I combine a custom tour with international flights?", acceptedAnswer: { "@type": "Answer", text: "Yes. Most clients book both their international flights and the in-destination tour package together. We coordinate everything — arrivals, transfers, activities, and accommodations — into a single, seamless plan." } },
    { "@type": "Question", name: "How do I request a custom tour package from Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your destination ideas, travel dates, number of travelers, budget range, and travel style — we'll build something around exactly that." } },
  ],
});

const tourTypes = [
  { icon: Compass, title: "Cultural immersion tours", desc: "Deep-dive into local history, cuisine, and traditions with expert local guides." },
  { icon: Heart, title: "Honeymoon and anniversary trips", desc: "Romantic itineraries for couples — curated experiences and special touches throughout." },
  { icon: Users, title: "Multi-generational family trips", desc: "Itineraries that work for grandparents, parents, and children traveling together." },
  { icon: Sparkles, title: "Adventure and expedition travel", desc: "Trekking, safari, polar expeditions, and active travel with expert-guided experiences." },
  { icon: Calendar, title: "Festival and event travel", desc: "Travel built around a specific event — a wedding, a festival, a sporting event." },
  { icon: Map, title: "Multi-country itineraries", desc: "Seamless travel across two or more countries with coordinated logistics throughout." },
];

const whatWeHandle = [
  "Destination research and itinerary design tailored to your interests",
  "Hotel and resort selection at your preferred budget tier",
  "Airport transfers and private transport coordination",
  "Guided tour bookings with local experts",
  "Activity and excursion planning (safaris, boat tours, cooking classes, etc.)",
  "Restaurant and dining recommendations",
  "Visa and entry requirement guidance for each destination",
  "International and domestic flight coordination",
  "24/7 support contact throughout your trip",
  "Emergency backup planning and travel insurance guidance",
];

const steps = [
  { num: "01", title: "Share your vision", desc: "Tell us your destination, dates, budget, group size, and what kind of trip you're after. No need to have it all figured out — we'll guide you." },
  { num: "02", title: "We design the itinerary", desc: "Our team researches and builds a custom day-by-day plan — hotels, transfers, activities — tailored specifically to your travelers." },
  { num: "03", title: "You review and refine", desc: "We present the itinerary and you give feedback. We adjust the pacing, hotels, activities, and budget until it's exactly what you want." },
  { num: "04", title: "We book everything", desc: "Once approved, we handle all the bookings and confirmations. You receive a complete trip document with every detail." },
];

const faqs = [
  { q: "What is a custom tour package from Tripsyfy?", a: "A custom tour is a travel itinerary built specifically around your dates, destinations, budget, group size, and interests — unlike fixed group tours." },
  { q: "How long does it take to build a custom itinerary?", a: "We typically deliver an initial itinerary within 2–5 business days. We iterate until the plan is exactly right." },
  { q: "Can Tripsyfy build a custom tour for a family with children?", a: "Yes. We specialize in family travel — balancing adult and child-friendly activities, managing pace, and booking family-suitable accommodations." },
  { q: "What destinations does Tripsyfy design custom tours for?", a: "We build custom itineraries worldwide — India, Europe, Southeast Asia, Africa safaris, Japan, South America, the Caribbean, and more." },
  { q: "Can I combine a custom tour with international flights?", a: "Yes. Most clients book flights and the in-destination package together. We coordinate arrivals, transfers, activities, and accommodations into one seamless plan." },
  { q: "How do I request a custom tour package from Tripsyfy?", a: "Call +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your destination ideas, travel dates, budget, and group size." },
];

export default function CustomTourPackagesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Custom Tour Packages"
        description="Tripsyfy creates custom tour packages for families, couples, and groups — tailored itineraries to any destination worldwide with full planning and booking support."
        canonical="https://tripsyfy.com/custom-tour-packages"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Personalized Travel</p>
          <h1 className="heading-xl text-white mb-3">Custom Tour Packages</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy builds custom travel itineraries designed around your destination, budget, group, and travel style — not a template, but a trip built specifically for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Custom Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Types of Custom Tours</p>
              <h2 className="heading-lg text-primary">What kind of trip are you planning?</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tourTypes.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-white rounded-xl shadow-card p-6">
                  <div className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="text-sm font-bold text-primary mb-2">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h2 className="heading-lg text-primary mb-6">Everything from itinerary to booking</h2>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary mb-6">Four steps to your custom trip</h2>
              <div className="space-y-5">
                {steps.map(({ num, title, desc }) => (
                  <div key={num} className="flex gap-4">
                    <div className="text-3xl font-bold text-primary/10 leading-none w-10 shrink-0">{num}</div>
                    <div>
                      <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <WhatsAppCTA variant="banner" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">Custom Tour Packages</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="heading-lg text-white mb-3">Start building your custom trip</h2>
                <p className="text-white/70 text-base leading-relaxed">Tell us your destination, dates, and group size — we'll design the rest.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Custom Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm text-white/60">
            <Link to="/tours" className="hover:text-white transition-colors">→ Browse Tour Packages</Link>
            <Link to="/group-travel-planning" className="hover:text-white transition-colors">→ Group Travel Planning</Link>
            <Link to="/international-flight-booking" className="hover:text-white transition-colors">→ International Flights</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
